import { Trans, useTranslation } from "next-i18next";
import React, { useState } from "react";
import { AuthorizeResponse, Response, SSOResponse } from "sc-user-service-client/dist/auth";

import { getAppSettings } from "../../../common/app-settings";
import {
    Themes,
    isL1System,
    maximumAvatarId,
    sabaClubName,
    sabaMobileName,
    systemName,
    vGamingMobileName,
    vGamingName,
} from "../../../common/constants";
import { GetLocalizedErrorMessage } from "../../../helpers/notice-helper";
import { randomInteger } from "../../../helpers/number-helper";
import { createNickname } from "../../../services/auth-service";
import { updateThemeSetting } from "../../../services/user-setting-service";
import ProfilePicture from "../../profile-picture";
import BaseDialog from "../../shared/base-dialog";
import { ModalType } from "../../shared/base-dialog/type";
import FirstLoginContainer from "../container";
import TextBlock from "../text-block";
import NameGenerator from "./name-generator";
import Styles from "./register.module.scss";

type Props = {
    session: SSOResponse;
    handleStateLogin: (authorizeInfo: AuthorizeResponse) => void;
};

enum OrderAvatarID {
    "ascending" = "ascending",
    "descending" = "descending",
}

export default function Register({ session, handleStateLogin }: Props) {
    const { t } = useTranslation(["register", "common", "messages"]);

    const nameAuto = NameGenerator.generateName;
    const [name, updateNameState] = useState<string>(nameAuto);
    const [selectedAvatarId, setSelectedAvatarId] = useState<number>(randomInteger(1, maximumAvatarId));
    const [isValidUserName, setIsValidUserName] = useState<boolean>(true);
    const [isOpenGalleryAvatar, setIsOpenGalleryAvatar] = useState<boolean>(false);
    const [nameInValid, setNameInValid] = useState<string>("");

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (validateUsername(name)) {
            setIsValidUserName(false);
            setNameInValid(t("register:invalidUsername"));
            return;
        }
        const response = await createNickname(session, name, selectedAvatarId);
        const authorizeResponse = response as AuthorizeResponse;
        if (authorizeResponse && authorizeResponse.authorized) {
            updateThemeSetting(session);
            Object.assign(authorizeResponse.user, { settings: { theme: Themes.light } });
            handleStateLogin(authorizeResponse);
        } else {
            setNameInValid(t(GetLocalizedErrorMessage((response as Response).errorCode)));
            setIsValidUserName(false);
        }
    };

    const generateName = (event) => {
        event.preventDefault();
        setIsValidUserName(true);
        updateNameState(NameGenerator.generateName);
    };

    const validateUsername = (username: string) => {
        const regex = new RegExp("^[a-zA-Z0-9]+$");
        return username.length > 14 || username.length < 3 || username.match(regex) === null;
    };

    const onChangeUserName = (event) => {
        event.preventDefault();
        updateNameState(event.target.value);
    };

    const clearUserName = () => {
        updateNameState("");
        setIsValidUserName(true);
    };

    const openAvatarGallery = () => {
        setIsOpenGalleryAvatar(true);
    };

    const closeAvatarGallery = () => {
        setIsOpenGalleryAvatar(false);
    };
    const highlightSystemClass = Styles[`highlight-system-${getAppSettings().appType}`];

    return (
        <FirstLoginContainer>
            <div className={`${Styles["wrapper-welcome"]} ${highlightSystemClass}`}>
                <TextBlock>
                    <Trans t={t}>
                        {t(
                            `register:registerTitle1-${systemName}-apptype${getAppSettings().appType}`,
                            isL1System() ? { sabaMobileName: sabaMobileName } : { vGamingMobileName: vGamingMobileName }
                        )}
                    </Trans>
                </TextBlock>
                <TextBlock>
                    <Trans t={t}>
                        {t(
                            `register:registerTitle2-${systemName}-apptype${getAppSettings().appType}`,
                            isL1System()
                                ? { sabaMobileName: sabaMobileName, sabaClubName: sabaClubName }
                                : { vGamingMobileName: vGamingMobileName, vGamingName: vGamingName }
                        )}
                    </Trans>
                </TextBlock>
                <TextBlock>
                    <Trans t={t}>
                        {t(
                            `register:registerTitle3-${systemName}-apptype${getAppSettings().appType}`,
                            isL1System() ? { sabaClubName: sabaClubName } : { vGamingName: vGamingName }
                        )}
                    </Trans>
                </TextBlock>
            </div>
            <ProfilePicture avatarId={selectedAvatarId} isEditable={true} onClick={openAvatarGallery} data-testid="open-avatar-gallery" />

            <form className={Styles["login-form"]} onSubmit={(event) => handleSubmit(event)} data-testid="btn-form-submit">
                <label className={Styles["input-title"]}>{t("register:registerName")}</label>

                <div className={Styles["user-box"]}>
                    <div className={`form-group ${Styles["register-wrapper-input"]}`}>
                        <input
                            type="text"
                            className={`form-control ${Styles["custom-form-control"]}`}
                            value={name}
                            onChange={(event) => onChangeUserName(event)}
                            data-testid="input-username"
                        />
                        <span
                            className={`icon-close ${Styles["icon-cancel"]}`}
                            onClick={() => clearUserName()}
                            data-testid="btn-clear-username"
                        ></span>
                    </div>

                    {!isValidUserName && (
                        <div className={Styles["hint-wrapper"]}>
                            <BaseDialog type={ModalType.Center} isOpen={isOpenGalleryAvatar}>
                                <div
                                    className={`icon-close ${Styles["icon-close-hint"]}`}
                                    onClick={() => {
                                        setIsValidUserName(true);
                                    }}
                                ></div>
                                <div className={Styles["hint-content"]}>
                                    <ul className={Styles["note"]}>
                                        <li>{t("register:rule1")}</li>
                                        <li>{t("register:rule2")}</li>
                                        <li>{t("register:rule3")}</li>
                                    </ul>
                                </div>
                            </BaseDialog>
                        </div>
                    )}

                    <div>
                        <button
                            className={`${Styles["btn-green"]} ${Styles["btn-refresh"]}`}
                            onClick={(event) => generateName(event)}
                            type="button"
                            data-testid="btn-generate-username"
                        >
                            <span className="icon-refresh"></span>
                        </button>
                    </div>
                </div>
                {!isValidUserName && <label className={Styles["message-error"]}>{nameInValid}</label>}

                <button className={`${Styles["btn-red"]} ${Styles["btn-submit"]}`} type="submit">
                    {t("common:enterGameBtn")}
                </button>
            </form>

            {isOpenGalleryAvatar && (
                <AvatarGallery selectedAvatarId={selectedAvatarId} onSelectAvatar={setSelectedAvatarId} onClose={closeAvatarGallery} />
            )}
        </FirstLoginContainer>
    );
}

const AvatarGallery: React.FC<{ selectedAvatarId: number; onSelectAvatar: (imgId: number) => void; onClose: () => void }> = ({
    selectedAvatarId,
    onSelectAvatar,
    onClose,
}) => {
    const { t } = useTranslation(["register", "common"]);
    const [selectedId, setSelectedId] = useState<number>(selectedAvatarId);

    const loadAvatars = (order = OrderAvatarID.descending) => {
        const avatars = [];
        let ids = [];

        switch (order) {
            case OrderAvatarID.ascending:
                ids = Array.from({ length: maximumAvatarId - 1 }, (_, i) => i + 1);
                break;
            case OrderAvatarID.descending:
                ids = Array.from({ length: maximumAvatarId - 1 }, (_, i) => maximumAvatarId - 1 - i);
                break;
            default:
                console.error("Invalid order type specified");
                return [];
        }

        ids.forEach((index) => {
            avatars.push(<Avatar key={index} id={index} isSelected={index === selectedId} onClick={() => setSelectedId(index)} />);
        });

        return avatars;
    };

    const submit = () => {
        onSelectAvatar(selectedId);
        onClose();
    };

    return (
        <div className={Styles["gallery-wrapper"]}>
            <BaseDialog type={ModalType.Center} isOpen={true} onCloseModal={onClose}>
                <h3 className={Styles["gallery-title"]}>{t("register:changeAvatar")}</h3>
                <div className={`icon-close ${Styles["icon-close"]}`} onClick={onClose} data-testid="btn-close-gallery"></div>
                <div className={Styles["container-avatars"]}>{loadAvatars()}</div>
                <div className={Styles["list-actions"]}>
                    <button className="btn-grey" onClick={onClose} type="button">
                        {t("common:cancel")}
                    </button>
                    <button className="btn-red" onClick={submit} type="button" data-testid="btn-submit-gallery">
                        {t("common:ok")}
                    </button>
                </div>
            </BaseDialog>
        </div>
    );
};

const Avatar: React.FC<{ id: number; isSelected: boolean; onClick: () => void }> = ({ id, isSelected, onClick }) => {
    return (
        <div className={`${Styles["img-wrapper"]} ${isSelected ? Styles["border-highlight"] : ""}`} onClick={onClick}>
            <img src={`/static/images/avatar/${id}.png`} className="img-fluid" width="54" height="54" alt="Avatar" />
            {isSelected && <div className={`icon-success ${Styles["selected-img"]}`}></div>}
        </div>
    );
};
